// App.tsx
import { Routes, Route ,Navigate} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import SignIn from "./pages/AuthPages/SignIn";
import NotFound from "./pages/OtherPage/NotFound";
import Unauthorized from "./pages/OtherPage/Unauthorized";
import UserProfiles from "./pages/UserProfiles";
import AppLayout from "./layout/AppLayout";
import Home from "./pages/Dashboard/Home";
import SuperAdminDashboard from "./pages/Dashboard/SuperAdminDashboard";
import SuperAdminPortfolio from "./pages/Dashboard/SuperAdminPortfolio";
import SuperAdminProjectDetail from "./pages/Dashboard/SuperAdminProjectDetail";
import SuperAdminUserPerformance from "./pages/Dashboard/SuperAdminUserPerformance";
import TempSuperAdminDashboard from "./pages/Dashboard/TempSuperAdminDashboard";
import RoleLandingDashboard from "./pages/Dashboard/RoleLandingDashboard";
// import { appTitle } from "./config/environment"; // Removed unused import
import "./config/environment.test"; // Auto-run environment test in development

import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import AuthRedirect from "./components/AuthRedirect";
import ProtectedRoute from "./components/ProtectedRoute";
import { lazy, Suspense } from "react";
import UserManagement from "./features/user/pages/Users";
import UserDetails from "./features/user/pages/UserDetails";
import { Roles, RoleMenuConfig } from "./features/roles";
import { Customers, CustomerDetails } from "./features/customer";
import { Documents } from "./features/document";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "react-hot-toast";
import { ScrollToTop } from "./shared/ScrollToTop";
import LocationList from "./features/location/LocationList";
import ManufacturerMaster from "./features/manufacturer/ManufacturerMaster";
import CustomerGroupList from "./features/customerGroup/CustomerGroupList";
import AssetCatalog from "./features/asset/pages/AssetCatalog";
import DepartmentMasterPage from "./features/masters/departments/DepartmentMasterPage";
import { ProjectAssignments, ManageProjectResources, ProjectPlanningCalendar, AIProjectArchitect } from "./features/project";
import {
  EpcProcurementPlaceholder,
  EpcEngineeringPlaceholder,
  EpcProjectListPage,
  EpcProjectCreatePage,
  EpcProjectEditPage,
  EpcProjectDetailLayout,
  EpcProjectOverviewTab,
  EpcProjectScheduleTab,
  EpcProjectMppTab,
  EpcProjectTeamTab,
  EpcProjectDocumentsTab,
} from "./features/epc";
import { useEpcProjectDetail } from "./features/epc/project/EpcProjectDetailContext";

const EpcScheduleComparePage = lazy(() => import("./features/epc/schedule/compare/EpcScheduleComparePage"));
const TemplateDriftQueuePage = lazy(() => import("./features/epc/templates/TemplateDriftQueuePage"));
const RollingProposalQueuePage = lazy(() => import("./features/epc/standards/rolling/RollingProposalQueuePage"));
const EpcProjectArchitecture3DTab = lazy(() => import("./features/epc/architecture3d/EpcProjectArchitecture3DTab"));
const WindFarmStandalonePage = lazy(() => import("./features/epc/windDemo/WindFarmStandalonePage"));
const EpcSiteConstructionDemoPage = lazy(() => import("./features/epc/siteDemo/EpcSiteConstructionDemoPage"));
const PmoPortfolioDashboardPage = lazy(() => import("./features/epc/dashboards/PmoPortfolioDashboard/PmoPortfolioDashboardPage"));
const PmoProjectDashboardPage = lazy(() => import("./features/epc/dashboards/PmoProjectDashboard/PmoProjectDashboardPage"));
const ProjectHeadDashboardPage = lazy(() => import("./features/epc/dashboards/ProjectHeadDashboard/ProjectHeadDashboardPage"));
const DepartmentHeadDashboardPage = lazy(() => import("./features/epc/dashboards/DepartmentHeadDashboard/DepartmentHeadDashboardPage"));
const DepartmentManagerDashboardPage = lazy(() => import("./features/epc/dashboards/DepartmentManagerDashboard/DepartmentManagerDashboardPage"));
const OmsLocationDashboardPage = lazy(() => import("./features/oms/dashboard/OmsLocationDashboardPage"));
const AdminAnalyticsPage = lazy(() => import("./features/oms/adminAnalytics/AdminAnalyticsPage"));
const VersionHistoryAdminPage = lazy(() => import("./pages/admin/VersionHistory/VersionHistoryAdminPage"));
const EpcProjectWorkspaceLayout = lazy(() => import("./features/epc/project/workspace/EpcProjectWorkspaceLayout"));
const WorkspacePulsePage = lazy(() => import("./features/epc/project/workspace/pages/PulsePage/PulsePage"));
const WorkspaceScopedSchedulePage = lazy(() => import("./features/epc/project/workspace/pages/SchedulePage/ScopedSchedulePage"));
const WorkspaceBoardPage = lazy(() => import("./features/epc/project/workspace/pages/BoardPage/BoardPage"));
const WorkspaceResourcesPage = lazy(() => import("./features/epc/project/workspace/pages/ResourcesPage/ResourcesPage"));
const WorkspaceRisksPage = lazy(() => import("./features/epc/project/workspace/pages/RisksPage/RisksPage"));
const WorkspaceReportsPage = lazy(() => import("./features/epc/project/workspace/pages/ReportsPage/ReportsPage"));

// Project Head area
const PhCharterTab = lazy(() => import("./features/epc/projectHead/tabs/CharterTab"));
const PhDepartmentsTab = lazy(() => import("./features/epc/projectHead/tabs/DepartmentsTab"));
const PhMasterScheduleTab = lazy(() => import("./features/epc/projectHead/tabs/MasterScheduleTab"));
const PhMilestonesTab = lazy(() => import("./features/epc/projectHead/tabs/MilestonesTab"));
const PhRiskRegisterTab = lazy(() => import("./features/epc/projectHead/tabs/RiskRegisterTab"));
const PhResourcePlanTab = lazy(() => import("./features/epc/projectHead/tabs/ResourcePlanTab"));
const PhProcurementEngineeringTab = lazy(() => import("./features/epc/projectHead/tabs/ProcurementEngineeringTab"));
const PhReportsTab = lazy(() => import("./features/epc/projectHead/tabs/ReportsTab"));

// Department Head area
const DhOverviewTab = lazy(() => import("./features/epc/departmentHead/tabs/OverviewTab"));
const DhWorkPackageInboxTab = lazy(() => import("./features/epc/departmentHead/tabs/WorkPackageInboxTab"));
const DhDepartmentScheduleTab = lazy(() => import("./features/epc/departmentHead/tabs/DepartmentScheduleTab"));
const DhTeamTab = lazy(() => import("./features/epc/departmentHead/tabs/TeamTab"));
const DhManagerDetailTab = lazy(() => import("./features/epc/departmentHead/tabs/ManagerDetailTab"));
const DhQualitySafetyTab = lazy(() => import("./features/epc/departmentHead/tabs/QualitySafetyTab"));
const DhReportsTab = lazy(() => import("./features/epc/departmentHead/tabs/ReportsTab"));
const DhManagerTrackingTab = lazy(() => import("./features/epc/departmentHead/tabs/ManagerTrackingTab"));
const DhDepartmentDprTab = lazy(() => import("./features/epc/departmentHead/tabs/DepartmentDprTab"));

// Manager area — 2-tab workspace: DPR command surface · Board
const DmDprTab = lazy(() => import("./features/epc/manager/dpr/DprCommandTab"));
const DmLegacyPlanRedirect = lazy(() => import("./features/epc/manager/dpr/LegacyPlanRedirect"));
const DmBoardTab = lazy(() => import("./features/epc/manager/tabs/BoardTab"));

// Masters — day-plan template admin
const DayPlanTemplateMasterPage = lazy(() => import("./features/epc/templates/DayPlanTemplateMasterPage"));

// Masters — EPC standard-schedule admin (project categories + standard library)
const ProjectCategoryMasterPage = lazy(() => import("./features/epc/standards/ProjectCategoryMasterPage"));
const StandardScheduleListPage = lazy(() => import("./features/epc/standards/StandardScheduleListPage"));
const StandardScheduleEditorPage = lazy(() => import("./features/epc/standards/StandardScheduleEditorPage"));

import AmsProjectAssignments from "./features/amsProject/pages/resource-assignment/AmsProjectAssignments";
import AmsManageProjectResources from "./features/amsProject/pages/resource-assignment/AmsManageProjectResources";
import AmsProjectCalendar from "./features/amsProject/pages/planning/AmsProjectCalendar";
import ProjectListRouter from "./features/project/pages/routers/ProjectListRouter";
import ProjectCreateRouter from "./features/project/pages/routers/ProjectCreateRouter";
import ProjectEditRouter from "./features/project/pages/routers/ProjectEditRouter";
import ProjectViewRouter from "./features/project/pages/routers/ProjectViewRouter";
import { Tasks } from "./features/task";
import { Checkpoints, CheckpointDetails, BulkCheckpointFormPage, BulkUploadCheckpointsPage, AmsBulkCheckpointForm, AmsBulkUploadCheckpointsPage } from "./features/checkpoint";
import UoMMasters from "./features/uom/pages/UoMMasters";
import AuditTypeMasters from "./features/auditType/pages/AuditTypeMasters";
import FeaturesMenuPage from "./features/feature/FeaturesMenuPage";
import FeaturePermissionsPage from "./features/feature/pages/FeaturePermissionsPage";
import SetPasswordPage from "./components/auth/SetPasswordPage";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import { Approvals, ProjectApprovals, ProjectApprovalDashboard } from "./features/approval";
import TaskGenerationSettings from "./pages/Settings/TaskGenerationSettings";
import PreventiveMaintenanceReport from "./pages/Reports/PreventiveMaintenanceReport";
import PreviewReport from "./pages/Reports/PreviewReport";
import WorkLibraryAdminPage from "./pages/Epc/WorkLibraryAdminPage";

/* ------------------------------------------------------------------ */
/* Role-aware dispatchers for /epc/projects/:id/* tabs (R4)            */
/* ------------------------------------------------------------------ */

const TAB_FALLBACK = <div className="p-6 text-slate-500">Loading…</div>;

/** Manager lands on the DPR command surface; everyone else gets the admin overview. */
function OverviewRoute() {
  const { myCap, myCapResolved } = useEpcProjectDetail();
  if (!myCapResolved) return TAB_FALLBACK;
  if (myCap === 'manager') {
    return <Navigate to="../dpr" replace />;
  }
  return <EpcProjectOverviewTab />;
}

/** Manager has no schedule tab anymore (Board covers it); governance/others get the master schedule tab. */
function ScheduleRoute() {
  const { myCap, myCapResolved } = useEpcProjectDetail();
  if (!myCapResolved) return TAB_FALLBACK;
  if (myCap === 'manager') {
    return <Navigate to="../board" replace />;
  }
  return <EpcProjectScheduleTab />;
}

/** Dept-head gets a department team view; everyone else gets the full team hierarchy. */
function TeamRoute() {
  const { myCap, myCapResolved } = useEpcProjectDetail();
  if (!myCapResolved) return TAB_FALLBACK;
  if (myCap === 'dept_head') {
    return (
      <Suspense fallback={TAB_FALLBACK}>
        <DhTeamTab />
      </Suspense>
    );
  }
  return <EpcProjectTeamTab />;
}

/** Reports tab is role-aware: PH / DH have reports modules; DM has none (2-tab workspace). */
function ReportsRoute() {
  const { myCap, mine, myCapResolved } = useEpcProjectDetail();
  if (!myCapResolved) return TAB_FALLBACK;
  if (myCap === 'manager') {
    return <Navigate to="../dpr" replace />;
  }
  if (myCap === 'dept_head') {
    return (
      <Suspense fallback={TAB_FALLBACK}>
        <DhReportsTab />
      </Suspense>
    );
  }
  if (mine?.role === 'project_head' && myCap === 'governance') {
    return (
      <Suspense fallback={TAB_FALLBACK}>
        <PhReportsTab />
      </Suspense>
    );
  }
  return null;
}

/** Default landing tab depends on role — PH lands on charter, DH on dh-overview, DM on dpr, others on overview. */
function DetailIndexRedirect() {
  const { myCap, mine, myCapResolved } = useEpcProjectDetail();
  // Never redirect off an unresolved cap — 'outsider' during load used to send
  // DM/DH users to the governance overview.
  if (!myCapResolved) return TAB_FALLBACK;
  if (myCap === 'dept_head') return <Navigate to="dh-overview" replace />;
  if (myCap === 'manager') return <Navigate to="dpr" replace />;
  if (mine?.role === 'project_head' && myCap === 'governance') {
    return <Navigate to="charter" replace />;
  }
  return <Navigate to="overview" replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ScrollToTop />
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="light"
          toastClassName="!font-semibold !text-base"
          style={{ zIndex: 999999 }}
        />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />

        <Routes>
          <Route path="/" element={<AuthRedirect />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/resetpassword" element={<ResetPasswordPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />

          <Route
            path="/demo/wind-farm"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen items-center justify-center bg-[#020617] p-8 text-cyan-300">
                      Loading wind farm demo…
                    </div>
                  }
                >
                  <WindFarmStandalonePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/demo/wind-construction" element={<Navigate to="/demo/wind-farm" replace />} />

          <Route
            path="/demo/epc-construction"
            element={
              <ProtectedRoute>
                <Suspense
                  fallback={
                    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8 text-teal-700">
                      Loading EPC construction demo…
                    </div>
                  }
                >
                  <EpcSiteConstructionDemoPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route element={<AppLayout />}>
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleLandingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/oms/locations/:locationId"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading location dashboard…</div>}>
                    <OmsLocationDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading admin analytics…</div>}>
                    <AdminAnalyticsPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/version-history"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading version history…</div>}>
                    <VersionHistoryAdminPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/masters"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectListRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/head"
              element={
                <ProtectedRoute roles={["head"]}>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/manager"
              element={
                <ProtectedRoute roles={["manager"]}>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/super-admin"
              element={
                <ProtectedRoute>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/super-admin/intelligence"
              element={
                <ProtectedRoute>
                  <TempSuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/temp-super-admin"
              element={
                <ProtectedRoute>
                  <TempSuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/super-admin/portfolio"
              element={
                <ProtectedRoute>
                  <SuperAdminPortfolio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/super-admin/projects/:projectId"
              element={
                <ProtectedRoute>
                  <SuperAdminProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/super-admin/users/:userId"
              element={
                <ProtectedRoute>
                  <SuperAdminUserPerformance />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute module="userManagement" action="read">
                  <UserManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users/:userId"
              element={
                <ProtectedRoute module="userManagement" action="read">
                  <UserDetails />
                </ProtectedRoute>
              }
            />



            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute module="roleManagement" action="read">
                  <Roles />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/roles/:roleId/menu"
              element={
                <ProtectedRoute module="roleManagement" action="read">
                  <RoleMenuConfig />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/features"
              element={
                <ProtectedRoute module="feature" action="read">
                  <FeaturesMenuPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/features/:featureKey/permissions"
              element={
                <ProtectedRoute module="feature" action="read">
                  <FeaturePermissionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feature-departments"
              element={<Navigate to="/admin/features" replace />}
            />

            <Route path="/profile" element={<UserProfiles />} />

            {/* Asset Catalog — merged Project Type + Asset master */}
            <Route
              path="/masters/asset-catalog"
              element={
                <ProtectedRoute module="assetMaster" action="read">
                  <AssetCatalog />
                </ProtectedRoute>
              }
            />

            {/* Departments — global EPC functional department master */}
            <Route
              path="/masters/departments"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <DepartmentMasterPage />
                </ProtectedRoute>
              }
            />

            {/* Day-Plan Templates — global EPC template master */}
            <Route
              path="/masters/day-plan-templates"
              element={
                <ProtectedRoute module="epcDayPlanTemplate" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading day-plan templates…</div>}>
                    <DayPlanTemplateMasterPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Project Categories — global EPC work sub-type master */}
            <Route
              path="/masters/project-categories"
              element={
                <ProtectedRoute module="epcStandardSchedule" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading categories…</div>}>
                    <ProjectCategoryMasterPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Standard Schedules — global EPC standard-schedule library */}
            <Route
              path="/masters/standard-schedules"
              element={
                <ProtectedRoute module="epcStandardSchedule" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading standards…</div>}>
                    <StandardScheduleListPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Standard Schedules — full-page tree editor for one standard */}
            <Route
              path="/masters/standard-schedules/:standardId"
              element={
                <ProtectedRoute module="epcStandardSchedule" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading standard…</div>}>
                    <StandardScheduleEditorPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Legacy routes now fold into the merged Asset Catalog */}
            <Route
              path="/masters/project-type"
              element={<Navigate to="/masters/asset-catalog" replace />}
            />

            <Route
              path="/masters/customer-group"
              element={
                <ProtectedRoute module="customerGroup" action="read">
                  <CustomerGroupList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/locations"
              element={
                <ProtectedRoute module="locationsMaster" action="read">
                  <LocationList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/manufacturers"
              element={
                <ProtectedRoute module="manufacturersMaster" action="read">
                  <ManufacturerMaster />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/customers"
              element={
                <ProtectedRoute module="customerMaster" action="read">
                  <Customers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/customers/:customerId"
              element={
                <ProtectedRoute module="customerMaster" action="read">
                  <CustomerDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/documents"
              element={
                <ProtectedRoute module="documentMaster" action="read">
                  <Documents />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/assets"
              element={<Navigate to="/masters/asset-catalog" replace />}
            />



            <Route
              path="/masters/checkpoints"
              element={
                <ProtectedRoute module="checkpointMaster" action="read">
                  <Checkpoints />
                </ProtectedRoute>
              }
            />


            <Route
              path="/masters/checkpoints/bulk"
              element={
                <ProtectedRoute module="checkpointMaster" action="create">
                  <BulkCheckpointFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/checkpoints/bulk-upload"
              element={
                <ProtectedRoute module="checkpointMaster" action="create">
                  <BulkUploadCheckpointsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/checkpoints/bulk-ams"
              element={
                <ProtectedRoute module="checkpointMaster" action="create">
                  <AmsBulkCheckpointForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/checkpoints/bulk-ams-upload"
              element={
                <ProtectedRoute module="checkpointMaster" action="create">
                  <AmsBulkUploadCheckpointsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/checkpoints/:id"
              element={
                <ProtectedRoute module="checkpointMaster" action="read">
                  <CheckpointDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/uom"
              element={
                <ProtectedRoute module="uomMaster" action="read">
                  <UoMMasters />
                </ProtectedRoute>
              }
            />

            <Route
              path="/masters/audit-types"
              element={
                <ProtectedRoute module="auditType" action="read">
                  <AuditTypeMasters />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/projectManagment"
              element={
                <ProtectedRoute module="projectMaster" action="read">
                  <ProjectListRouter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/:projectId/assignments"
              element={
                <ProtectedRoute module="projectMaster" action="update">
                  <ProjectAssignments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/:projectId/manage-resources"
              element={
                <ProtectedRoute module="projectMaster" action="update">
                  <ManageProjectResources />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/create"
              element={
                <ProtectedRoute module="projectMaster" action="create">
                  <ProjectCreateRouter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/ai-create"
              element={
                <ProtectedRoute module="projectMaster" action="create">
                  <AIProjectArchitect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/edit/:projectId"
              element={
                <ProtectedRoute module="projectMaster" action="update">
                  <ProjectEditRouter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/view/:projectId"
              element={
                <ProtectedRoute module="projectMaster" action="read">
                  <ProjectViewRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/calendar"
              element={
                <ProtectedRoute module="projectMaster" action="read">
                  <ProjectPlanningCalendar />
                </ProtectedRoute>
              }
            />

            {/* AMS-specific routes — use AMS services, separate from OMS variants */}
            <Route
              path="/ams-projects/:projectId/assignments"
              element={
                <ProtectedRoute module="projectMaster" action="read">
                  <AmsProjectAssignments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ams-projects/:projectId/manage-resources"
              element={
                <ProtectedRoute module="projectMaster" action="read">
                  <AmsManageProjectResources />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ams-projects/:projectId/calendar"
              element={
                <ProtectedRoute module="projectMaster" action="read">
                  <AmsProjectCalendar />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks"
              element={
                <ProtectedRoute module="taskExecution" action="read">
                  <Tasks />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks/history"
              element={
                <ProtectedRoute module="taskExecution" action="read">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Task History</h1>
                    <p className="text-gray-600">Task history coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks/approvals"
              element={
                <ProtectedRoute module="taskMaster" action="update">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Task Approvals</h1>
                    <p className="text-gray-600">Task approval center - pending approvals will be shown here</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Notifications</h1>
                    <p className="text-gray-600">Notification center - all your notifications will be shown here</p>
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-semibold text-blue-800">API Endpoints Available:</h3>
                      <ul className="mt-2 text-sm text-blue-600">
                        <li>• GET /api/v1/notifications/my - Get your notifications</li>
                        <li>• GET /api/v1/notifications/my/unread-count - Get unread count</li>
                        <li>• PATCH /api/v1/notifications/:id/mark-read - Mark as read</li>
                        <li>• PATCH /api/v1/notifications/my/mark-all-read - Mark all as read</li>
                      </ul>
                    </div>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/approvals"
              element={
                <ProtectedRoute>
                  <Approvals />
                </ProtectedRoute>
              }
            />

            <Route
              path="/approvals/projects"
              element={
                <ProtectedRoute>
                  <ProjectApprovals />
                </ProtectedRoute>
              }
            />

            <Route
              path="/approvals/dashboard"
              element={
                <ProtectedRoute>
                  <ProjectApprovalDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sites/supervisors"
              element={
                <ProtectedRoute module="siteSupervisor" action="read">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Site Supervisors</h1>
                    <p className="text-gray-600">Site supervisor management coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/sites/engineers"
              element={
                <ProtectedRoute module="siteEngineer" action="read">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Site Engineers</h1>
                    <p className="text-gray-600">Site engineer management coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/projects"
              element={
                <ProtectedRoute module="reports" action="read">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Project Reports</h1>
                    <p className="text-gray-600">Project reports coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/tasks"
              element={
                <ProtectedRoute module="reports" action="read">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Task Reports</h1>
                    <p className="text-gray-600">Task reports coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/assets"
              element={
                <ProtectedRoute module="reports" action="read">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Asset Reports</h1>
                    <p className="text-gray-600">Asset reports coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/preventive-maintenance"
              element={
                <ProtectedRoute module="reports" action="read" roles={["admin", "head", "manager"]}>
                  <PreventiveMaintenanceReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports/preventive-maintenance/preview"
              element={
                <ProtectedRoute module="reports" action="read" roles={["admin", "head", "manager"]}>
                  <PreviewReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/epc/procurement"
              element={
                <ProtectedRoute module="procurement" action="read">
                  <EpcProcurementPlaceholder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/engineering"
              element={
                <ProtectedRoute module="engineering" action="read">
                  <EpcEngineeringPlaceholder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/projects"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <EpcProjectListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/projects/new"
              element={
                <ProtectedRoute module="epcProject" action="create">
                  <EpcProjectCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/projects/:id/edit"
              element={
                <ProtectedRoute module="epcProject" action="update">
                  <EpcProjectEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/projects/:id"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <EpcProjectDetailLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DetailIndexRedirect />} />
              <Route path="overview" element={<OverviewRoute />} />
              <Route path="my-work" element={<Navigate to="../workspace/pulse" replace />} />
              <Route path="schedule" element={<ScheduleRoute />} />
              <Route path="mpp" element={<EpcProjectMppTab />} />
              <Route path="team" element={<TeamRoute />} />
              <Route path="documents" element={<EpcProjectDocumentsTab />} />
              <Route path="reports" element={<ReportsRoute />} />

              {/* Project Head tabs (R4) */}
              <Route
                path="charter"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhCharterTab />
                  </Suspense>
                }
              />
              <Route
                path="departments"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhDepartmentsTab />
                  </Suspense>
                }
              />
              <Route
                path="master-schedule"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhMasterScheduleTab />
                  </Suspense>
                }
              />
              <Route
                path="milestones"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhMilestonesTab />
                  </Suspense>
                }
              />
              <Route
                path="risks"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhRiskRegisterTab />
                  </Suspense>
                }
              />
              <Route
                path="resource-plan"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhResourcePlanTab />
                  </Suspense>
                }
              />
              <Route
                path="procurement-engineering"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <PhProcurementEngineeringTab />
                  </Suspense>
                }
              />

              {/* Department Head tabs (R4) */}
              <Route
                path="dh-overview"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhOverviewTab />
                  </Suspense>
                }
              />
              <Route path="my-department" element={<Navigate to="../dh-overview" replace />} />
              <Route
                path="work-package-inbox"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhWorkPackageInboxTab />
                  </Suspense>
                }
              />
              <Route
                path="department-schedule"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhDepartmentScheduleTab />
                  </Suspense>
                }
              />
              <Route
                path="quality-safety"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhQualitySafetyTab />
                  </Suspense>
                }
              />
              <Route
                path="manager-tracking"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhManagerTrackingTab />
                  </Suspense>
                }
              />
              <Route
                path="manager/:managerId"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhManagerDetailTab />
                  </Suspense>
                }
              />
              <Route
                path="department-dpr"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DhDepartmentDprTab />
                  </Suspense>
                }
              />

              {/* Department Manager tabs (R4) — overview & schedule handled by dispatchers above */}
              <Route path="planning" element={<Navigate to="../dpr" replace />} />
              <Route
                path="planning/:taskKey"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DmLegacyPlanRedirect />
                  </Suspense>
                }
              />
              <Route path="day-planner" element={<Navigate to="../dpr" replace />} />
              <Route
                path="dpr"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DmDprTab />
                  </Suspense>
                }
              />
              <Route path="tasks" element={<Navigate to="../dpr" replace />} />
              <Route path="engineers" element={<Navigate to="../dpr" replace />} />
              <Route path="history" element={<Navigate to="../dpr" replace />} />
              <Route
                path="board"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <DmBoardTab />
                  </Suspense>
                }
              />
              <Route
                path="project-3d"
                element={
                  <Suspense
                    fallback={
                      <div className="flex min-h-[200px] items-center justify-center p-6 text-gray-600">Loading 3D overview…</div>
                    }
                  >
                    <EpcProjectArchitecture3DTab />
                  </Suspense>
                }
              />
              <Route
                path="workspace"
                element={
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <EpcProjectWorkspaceLayout />
                  </Suspense>
                }
              >
                <Route index element={<Navigate to="pulse" replace />} />
                <Route
                  path="pulse"
                  element={
                    <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                      <WorkspacePulsePage />
                    </Suspense>
                  }
                />
                <Route
                  path="schedule"
                  element={
                    <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                      <WorkspaceScopedSchedulePage />
                    </Suspense>
                  }
                />
                <Route
                  path="board"
                  element={
                    <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                      <WorkspaceBoardPage />
                    </Suspense>
                  }
                />
                <Route
                  path="resources"
                  element={
                    <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                      <WorkspaceResourcesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="risks"
                  element={
                    <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                      <WorkspaceRisksPage />
                    </Suspense>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                      <WorkspaceReportsPage />
                    </Suspense>
                  }
                />
              </Route>
            </Route>

            {/* PH/DH/DM top-level project route trees removed (R3) — consolidated under /epc/projects/:id/* via R4 */}

            {/* Full-page MPP re-import compare screen (sibling to the nested schedule tab). */}
            <Route
              path="/epc/projects/:projectId/schedule/compare/:stagedImportId"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <EpcScheduleComparePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Day-plan template regeneration queue — drift after re-import / template publish. */}
            <Route
              path="/epc/projects/:projectId/schedule/template-drift"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <TemplateDriftQueuePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Rolling adaptive-engine proposal queue — nightly re-flow / ahead flags. */}
            <Route
              path="/epc/projects/:projectId/rolling-plan/proposals"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
                    <RollingProposalQueuePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="/epc/dashboards/pmo"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading dashboard…</div>}>
                    <PmoPortfolioDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/dashboards/pmo/projects/:projectId"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading dashboard…</div>}>
                    <PmoProjectDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/dashboards/project-head"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading dashboard…</div>}>
                    <ProjectHeadDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/dashboards/department-head"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading dashboard…</div>}>
                    <DepartmentHeadDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/epc/dashboards/department-manager"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <Suspense fallback={<div className="p-6 text-slate-500">Loading dashboard…</div>}>
                    <DepartmentManagerDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="/epc/work-library"
              element={
                <ProtectedRoute module="epcProject" action="read">
                  <WorkLibraryAdminPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <div className="p-6">
                    <h1 className="text-2xl font-bold mb-4">Settings</h1>
                    <p className="text-gray-600">Settings page coming soon...</p>
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/task-generation"
              element={
                <ProtectedRoute>
                  <TaskGenerationSettings />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </ErrorBoundary>
  );
}